import { FC, useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@frontegg/react'
import { useFormik } from 'formik'

/* Utils */
import { hasAnyRole, Role } from 'lib/utils'
import { useApplicationSetup } from './useApplicationSetup'
import { ApplicationViewState } from './applicationViewState'
import { connectionErrorMessage } from './applicationProviders'
import { ambiguousSites } from './utils'
import {
  APPLICATION_INSTRUCTION_PLACEHOLDERS,
  APPLICATIONS_PATH,
  APPLICATION_STEPS,
  APPROVAL_FAILED_TITLE,
  APPROVAL_UNFINISHED_MESSAGE,
  APPROVAL_UNFINISHED_TITLE,
  CONFIGURE_DETAILS_DESCRIPTION,
  CONNECTION_DETAILS_DESCRIPTION,
  CONNECTION_DETAILS_TITLE,
  ConfigureFormValues,
  PLACEHOLDER_DOCUMENTATION,
  configureFormSchema
} from './applicationSetup.constants'

/* Components */
import IntegrationHeader from 'ui/components/header/IntegrationHeader'
import HorizontalStepper from 'ui/components/stepper'
import { IntegrationsContainer } from '../setupIntegrationInstance.styles'
import InstructionsPane from '../jira-integration/onboarding-wizard/components/InstructionsPane'
import { ApplicationBody, ApplicationFormColumn, ApplicationRightPane } from './applicationSetup.styles'
import ConnectStep, { RetryPrompt } from './components/ConnectStep'
import ConfigureStep from './components/ConfigureStep'
import UnknownIntegration from './components/UnknownIntegration'

/* The setup page for every outbound integration: the manifest slug in the address picks the
 * catalog entry, which carries the permissions, and its OAuth provider picks the wording. */
const SetupApplicationInstance: FC = () => {
  const navigate = useNavigate()
  const { user: authUser } = useAuth()
  const { manifest, application, copy, viewState, busy, pageError, takenNames, connect, retry, cancel, save } =
    useApplicationSetup()

  const noEditPermission = useMemo(() => hasAnyRole([Role.Viewer, Role.Demo], authUser), [authUser])

  const goToApplications = () => navigate(APPLICATIONS_PATH)

  /* The name lives here rather than in the step, because the header's Save is what submits it. */
  const validationSchema = useMemo(() => configureFormSchema(takenNames), [takenNames])
  const formik = useFormik<ConfigureFormValues>({
    initialValues: { name: application?.display_name ?? '' },
    enableReinitialize: true,
    validationSchema,
    onSubmit: () => undefined
  })

  const handleSave = async () => {
    const message = await save(formik.values.name)
    if (message) formik.setFieldError('name', message)
  }

  const isConfigure = viewState === ApplicationViewState.Configure

  /* Step one gives way to the reason and Try again once an instance exists without an approval. */
  const retryPrompt: RetryPrompt | undefined =
    viewState === ApplicationViewState.PendingReturn
      ? { title: APPROVAL_UNFINISHED_TITLE, message: APPROVAL_UNFINISHED_MESSAGE, sites: [], onRetry: retry }
      : viewState === ApplicationViewState.Failed
      ? {
          title: APPROVAL_FAILED_TITLE,
          message: connectionErrorMessage(copy, application?.connection?.error_code ?? null),
          sites: ambiguousSites(application?.connection),
          onRetry: retry
        }
      : undefined

  /* Not found has already asked the router for the fresh-start route, so it renders nothing while
   * that navigation settles. */
  if (viewState === ApplicationViewState.Loading || viewState === ApplicationViewState.NotFound) {
    return <IntegrationsContainer />
  }

  if (!manifest || !copy) return <UnknownIntegration onBack={goToApplications} />

  return (
    <IntegrationsContainer>
      <IntegrationHeader
        headerTitle={`Add ${manifest.display_name} Integration Instance`}
        handleSave={handleSave}
        handleDiscard={cancel}
        /* Leaving before Save must not keep the approved instance, so Back confirms and removes it like Cancel. */
        handleBack={cancel}
        discardDisabled={false}
        noEditPermission={noEditPermission}
        saveDisabled={!isConfigure || busy || !formik.isValid}
      />

      <ApplicationBody>
        <ApplicationFormColumn>
          <HorizontalStepper steps={APPLICATION_STEPS} activeStep={isConfigure ? 1 : 0} />

          <Box>
            <Typography className="section-title">{CONNECTION_DETAILS_TITLE}</Typography>
            <Typography className="section-description">
              {isConfigure ? CONFIGURE_DETAILS_DESCRIPTION : CONNECTION_DETAILS_DESCRIPTION}
            </Typography>
          </Box>

          {isConfigure && application?.connection ? (
            <ConfigureStep
              copy={copy}
              connection={application.connection}
              permissionScopes={manifest.config?.permission_scopes ?? []}
              formik={formik}
              busy={busy}
              noEditPermission={noEditPermission}
            />
          ) : (
            <ConnectStep
              copy={copy}
              permissionScopes={manifest.config?.permission_scopes ?? []}
              takenNames={takenNames}
              busy={busy}
              pageError={pageError}
              noEditPermission={noEditPermission}
              onConnect={connect}
              retry={retryPrompt}
            />
          )}
        </ApplicationFormColumn>

        <ApplicationRightPane>
          <InstructionsPane
            integration={PLACEHOLDER_DOCUMENTATION}
            instructions={APPLICATION_INSTRUCTION_PLACEHOLDERS}
          />
        </ApplicationRightPane>
      </ApplicationBody>
    </IntegrationsContainer>
  )
}

export default SetupApplicationInstance
